/**
 * Analyzer: Setup Bar Context
 *
 * Bar-grounded observations scoped to a SINGLE setup name. Different from
 * `dayBarContext` (one day, all setups) and `progressBarPatterns` (all
 * setups, last N days) — this one zooms into a specific setup's full
 * tracked history and asks: under what conditions does THIS setup's edge
 * actually fire?
 *
 * Three patterns this round:
 *
 *   1. Stack-alignment WR gap. Compares with-stack vs against-stack WR
 *      for trades of this setup. If there's a meaningful gap, surface it
 *      so the trader knows when their setup's edge concentrates.
 *
 *   2. MFE leakage on the setup's winners. The capture ratio across this
 *      setup's winners — distinct from the day-level pattern because it
 *      reflects setup-specific exit discipline.
 *
 *   3. Setup decay. Compares the recent half (last 14 days, by default)
 *      against the prior half (15-30 days ago) — flags setups whose WR
 *      has dropped meaningfully. Acts as an early-warning that the
 *      market's moved past the pattern's edge.
 *
 * Reads:
 *   trade.setup, trade.netPnl, trade.entry, trade.direction, trade.date
 *   trade.barAnalysis.{maeR, mfeR, mfeDollars, emasAtEntry}
 *
 * Trades without barAnalysis are silently skipped — backfill (Settings →
 * Charts → Run backfill) is the prerequisite for full signal here.
 */

import { CATEGORY, SEVERITY, makeObservation, makeAction, ACTION_TYPE } from '../../types.js';

export const key = 'setupBarContext';
export const tier = 'free';

// Thresholds — same conservative shape as dayBarContext's alignment patterns
const STACK_GAP_MIN_TRADES   = 3;
const STACK_GAP_MIN_WR_GAP   = 0.20;
const LEAKAGE_MIN_WINNERS    = 4;
const LEAKAGE_MAX_AVG_CAPTURE = 0.40;
const DECAY_MIN_TRADES_PER_HALF = 3;
const DECAY_MIN_WR_DROP      = 0.20;
const DECAY_RECENT_DAYS      = 14;

const fmtPct = (n) => `${Math.round(n * 100)}%`;

const ba = (t) => {
  const a = t?.barAnalysis;
  if (!a || typeof a !== 'object') return null;
  if (!Number.isFinite(a.maeR) || !Number.isFinite(a.mfeR)) return null;
  return a;
};

// ── Period-list-aware stack classifier (mirrors dayBarContext) ────────────
const classifyStack = (emasAtEntry, direction) => {
  if (!emasAtEntry || typeof emasAtEntry !== 'object') return 'neutral';
  const periods = Object.keys(emasAtEntry)
    .map(k => parseInt(k, 10))
    .filter(n => Number.isFinite(n) && n >= 1)
    .sort((a, b) => a - b);
  if (periods.length < 2) return 'neutral';
  const values = periods.map(p => emasAtEntry[p] ?? emasAtEntry[String(p)]);
  if (!values.every(v => Number.isFinite(v))) return 'neutral';
  let bullish = true, bearish = true;
  for (let i = 0; i < values.length - 1; i++) {
    if (values[i] <= values[i + 1]) bullish = false;
    if (values[i] >= values[i + 1]) bearish = false;
  }
  if (!bullish && !bearish) return 'neutral';
  const isLong = direction !== 'short';
  if (bullish) return isLong ? 'with' : 'against';
  return isLong ? 'against' : 'with';
};

// ── Pattern 1: Stack-alignment WR gap ─────────────────────────────────────

const detectStackAlignmentEdge = (setupName, trades) => {
  const classified = trades
    .map(t => ({ trade: t, stance: classifyStack(ba(t)?.emasAtEntry, t.direction) }))
    .filter(c => c.stance !== 'neutral');

  const withStack    = classified.filter(c => c.stance === 'with');
  const againstStack = classified.filter(c => c.stance === 'against');
  if (withStack.length < STACK_GAP_MIN_TRADES || againstStack.length < STACK_GAP_MIN_TRADES) return null;

  const wr = (group) => group.length === 0 ? 0
    : group.filter(c => c.trade.netPnl > 0).length / group.length;
  const withWR    = wr(withStack);
  const againstWR = wr(againstStack);
  const gap = Math.abs(withWR - againstWR);
  if (gap < STACK_GAP_MIN_WR_GAP) return null;

  // Which side wins determines the framing
  const withBetter = withWR > againstWR;

  return makeObservation({
    id: `${key}:${setupName}:stack-edge`,
    analyzer: key,
    scope: 'setup',
    scopeId: setupName,
    severity: withBetter ? SEVERITY.SUCCESS : SEVERITY.WARNING,
    category: CATEGORY.SETUP,
    title: withBetter
      ? `"${setupName}" edge concentrates with the EMA stack`
      : `"${setupName}" performs better AGAINST the stack — counter-trend setup`,
    detail: `${withStack.length} stack-aligned trades: ${fmtPct(withWR)} WR. ${againstStack.length} against-stack: ${fmtPct(againstWR)} WR. Gap of ${fmtPct(gap)}.`,
    action: withBetter
      ? `Add a checklist line for this setup: "Is the stack aligned in my direction?" Skip the setup when the answer is no.`
      : `Lean into this as a counter-trend setup specifically — if you're taking it with the stack, you're missing its edge.`,
    actions: [
      makeAction({
        type: ACTION_TYPE.ADD_RULE_TO_SETUP,
        label: `Add rule to "${setupName}"`,
        payload: {
          setupName,
          rule: withBetter
            ? `Confirm the EMA stack agrees with my direction (skip if not)`
            : `Confirm this is a counter-trend play — entry-TF EMA stack should oppose my direction`,
        },
        confirmText: `Add this rule to "${setupName}"?`,
        doneLabel: 'Rule added ✓',
      }),
    ],
    evidence: {
      withCount: withStack.length,
      againstCount: againstStack.length,
      withWR,
      againstWR,
      gap,
    },
    confidence: 0.82,
    priority: 78
  });
};

// ── Pattern 1b: Per-HTF stack-alignment WR gap ────────────────────────────
//
// Same shape as Pattern 1 but evaluated against barAnalysis.htfEmas[tf]
// instead of the entry-timeframe stack. The trader workflow asks "does
// MMXM work better when the 4H stack agrees?" — this answers it per TF
// across the setup's full history.
//
// Each HTF that meets the sample-size + gap threshold produces its own
// observation. So a setup with a strong 4H edge and a weak 1H edge will
// surface "MMXM concentrates edge with the 4H stack" plus, separately,
// nothing for 1H — the silence itself is information.

const detectHtfStackEdgeForTf = (setupName, trades, tf) => {
  const classified = trades
    .map(t => ({ trade: t, stance: classifyStack(ba(t)?.htfEmas?.[tf], t.direction) }))
    .filter(c => c.stance !== 'neutral');

  const withStack    = classified.filter(c => c.stance === 'with');
  const againstStack = classified.filter(c => c.stance === 'against');
  if (withStack.length < STACK_GAP_MIN_TRADES || againstStack.length < STACK_GAP_MIN_TRADES) return null;

  const wr = (group) => group.length === 0 ? 0
    : group.filter(c => c.trade.netPnl > 0).length / group.length;
  const withWR    = wr(withStack);
  const againstWR = wr(againstStack);
  const gap = Math.abs(withWR - againstWR);
  if (gap < STACK_GAP_MIN_WR_GAP) return null;

  const withBetter = withWR > againstWR;
  const tfLabel = tf.toUpperCase();

  // Build the structured rule the trader can one-click add to the setup's
  // checklist when this is an EDGE pattern (with-stack wins). For
  // AGAINST-stack edges (counter-TF setups), the rule is the opposite —
  // "is the 4H stack against me?" is what they'd want to gate on.
  const ruleText = withBetter
    ? `Confirm the ${tfLabel} stack agrees with my direction (skip if not)`
    : `Confirm the ${tfLabel} stack opposes my direction (this setup is a counter-${tfLabel} play)`;

  return makeObservation({
    id: `${key}:${setupName}:htf-stack-edge:${tf}`,
    analyzer: key,
    scope: 'setup',
    scopeId: setupName,
    severity: withBetter ? SEVERITY.SUCCESS : SEVERITY.WARNING,
    category: CATEGORY.SETUP,
    title: withBetter
      ? `"${setupName}" edge concentrates with the ${tfLabel} stack`
      : `"${setupName}" performs better AGAINST the ${tfLabel} — counter-${tfLabel} setup`,
    detail: `${withStack.length} ${tfLabel}-aligned trades: ${fmtPct(withWR)} WR. ${againstStack.length} against-${tfLabel}: ${fmtPct(againstWR)} WR. Gap of ${fmtPct(gap)}.`,
    action: withBetter
      ? `Add a hard checklist gate for this setup: "Is the ${tfLabel} stack in my direction?" Skip when no.`
      : `Treat this as a deliberate counter-${tfLabel} play — taking it with the ${tfLabel} stack is missing its edge.`,
    actions: [
      makeAction({
        type: ACTION_TYPE.ADD_RULE_TO_SETUP,
        label: `Add rule to "${setupName}"`,
        payload: { setupName, rule: ruleText },
        confirmText: `Add this rule to "${setupName}"?\n\n  "${ruleText}"`,
        doneLabel: 'Rule added ✓',
      }),
    ],
    evidence: {
      tf,
      withCount: withStack.length,
      againstCount: againstStack.length,
      withWR,
      againstWR,
      gap,
    },
    // Higher-TF observations get higher priority because they reflect more
    // structural edge — fighting the 4H is a more expensive mistake than
    // fighting 15M. Order: 4h > 1h > 15m, falling back to 78 (entry-TF level).
    priority: tf === '4h' ? 84 : tf === '1h' ? 82 : tf === '15m' ? 80 : 78,
    confidence: 0.82,
  });
};

const detectHtfStackEdge = (setupName, trades) => {
  // Discover which HTFs we have data for across this setup's trades.
  const htfsPresent = new Set();
  for (const t of trades) {
    const map = ba(t)?.htfEmas;
    if (map && typeof map === 'object') {
      for (const tf of Object.keys(map)) htfsPresent.add(tf);
    }
  }
  if (htfsPresent.size === 0) return [];

  // Run the WR-gap check per TF; return whichever fired (could be 0+).
  const out = [];
  // Stable order so test snapshots and observation panels are deterministic.
  const tfOrder = ['15m', '30m', '1h', '2h', '4h'];
  const ordered = [...htfsPresent].sort((a, b) => {
    const ai = tfOrder.indexOf(a);
    const bi = tfOrder.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
  for (const tf of ordered) {
    const obs = detectHtfStackEdgeForTf(setupName, trades, tf);
    if (obs) out.push(obs);
  }
  return out;
};

// ── Pattern 2: MFE leakage on the setup's winners ─────────────────────────

const detectSetupMfeLeakage = (setupName, trades) => {
  const winners = trades.filter(t => {
    const a = ba(t);
    if (!a) return false;
    if (!Number.isFinite(t.netPnl) || t.netPnl <= 0) return false;
    return Number.isFinite(a.mfeDollars) && a.mfeDollars > 0;
  });
  if (winners.length < LEAKAGE_MIN_WINNERS) return null;

  let sum = 0;
  let worst = null;
  let worstCapture = 1;
  for (const t of winners) {
    const cap = Math.max(0, Math.min(1, t.netPnl / ba(t).mfeDollars));
    sum += cap;
    if (cap < worstCapture) { worstCapture = cap; worst = t; }
  }
  const avgCapture = sum / winners.length;
  if (avgCapture >= LEAKAGE_MAX_AVG_CAPTURE) return null;

  // If we identified a specific worst-capture trade, surface a Jump action
  // so the trader can review it directly without hunting through the journal.
  const jumpActions = worst?.id
    ? [makeAction({
        type: ACTION_TYPE.JUMP_TO_TRADE,
        label: 'Show worst trade',
        payload: { tradeId: worst.id, date: worst.date || null },
        doneLabel: 'Jumping…',
      })]
    : null;

  return makeObservation({
    id: `${key}:${setupName}:mfe-leakage`,
    analyzer: key,
    scope: 'setup',
    scopeId: setupName,
    severity: SEVERITY.WARNING,
    category: CATEGORY.EXECUTION,
    title: `"${setupName}" winners cap at ${fmtPct(avgCapture)} of MFE`,
    detail: `Across ${winners.length} winning ${setupName} trades, average exit captured ${fmtPct(avgCapture)} of the available favorable move. Worst case: ${fmtPct(worstCapture)}.`,
    action: `For this setup specifically, try a partial-exit at your usual target + runner with trailing stop. Compare 30d MFE capture afterward.`,
    actions: jumpActions,
    evidence: {
      avgCapture,
      winnerCount: winners.length,
      worstTradeId: worst?.id ?? null,
      worstCapture
    },
    confidence: 0.8,
    priority: 70
  });
};

// ── Pattern 3: Setup decay — recent half vs prior half ────────────────────

const detectSetupDecay = (setupName, trades, anchorDate) => {
  if (!anchorDate || !Array.isArray(trades) || trades.length === 0) return null;

  // Anchor is "today" trading-day. Recent = anchorDate - DECAY_RECENT_DAYS to anchorDate.
  // Prior = anchorDate - 2*DECAY_RECENT_DAYS to anchorDate - DECAY_RECENT_DAYS.
  // String-compare YYYY-MM-DD works because of zero-padding.
  const dateMinusDays = (dateStr, days) => {
    const [y, m, d] = dateStr.split('-').map(n => parseInt(n, 10));
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() - days);
    return dt.toISOString().slice(0, 10);
  };
  const recentFrom = dateMinusDays(anchorDate, DECAY_RECENT_DAYS);
  const priorFrom  = dateMinusDays(anchorDate, DECAY_RECENT_DAYS * 2);

  const recent = trades.filter(t => t.date && t.date >= recentFrom && t.date <= anchorDate);
  const prior  = trades.filter(t => t.date && t.date >= priorFrom && t.date < recentFrom);
  if (recent.length < DECAY_MIN_TRADES_PER_HALF) return null;
  if (prior.length  < DECAY_MIN_TRADES_PER_HALF) return null;

  const wr = (group) => group.filter(t => Number.isFinite(t.netPnl) && t.netPnl > 0).length / group.length;
  const recentWR = wr(recent);
  const priorWR  = wr(prior);
  const drop = priorWR - recentWR;
  if (drop < DECAY_MIN_WR_DROP) return null;

  return makeObservation({
    id: `${key}:${setupName}:decay`,
    analyzer: key,
    scope: 'setup',
    scopeId: setupName,
    severity: SEVERITY.WARNING,
    category: CATEGORY.SETUP,
    title: `"${setupName}" decaying — WR dropped from ${fmtPct(priorWR)} to ${fmtPct(recentWR)}`,
    detail: `Last ${DECAY_RECENT_DAYS} days: ${recent.filter(t => t.netPnl > 0).length}/${recent.length} winners. Prior ${DECAY_RECENT_DAYS} days: ${prior.filter(t => t.netPnl > 0).length}/${prior.length}. Setup may be losing edge.`,
    action: `Pull the recent losers and the prior winners side by side. What's different? Could be regime change, your execution, or both.`,
    evidence: {
      recentWR,
      priorWR,
      drop,
      recentCount: recent.length,
      priorCount: prior.length,
    },
    confidence: 0.78,
    priority: 75
  });
};

// ── Public analyze() ──────────────────────────────────────────────────────

export const analyze = (ctx) => {
  const { setupName, setupTrades, anchorDate } = ctx || {};
  if (!setupName || !Array.isArray(setupTrades) || setupTrades.length === 0) return null;

  const out = [];

  const stackEdge = detectStackAlignmentEdge(setupName, setupTrades);
  if (stackEdge) out.push(stackEdge);

  // Per-HTF stack edges — one observation per timeframe that has a
  // meaningful WR gap. Surfaced in addition to the entry-TF pattern
  // because they answer a different question ("which structural frame
  // matters for this setup") and they're often more actionable.
  const htfEdges = detectHtfStackEdge(setupName, setupTrades);
  for (const o of htfEdges) out.push(o);

  const leakage = detectSetupMfeLeakage(setupName, setupTrades);
  if (leakage) out.push(leakage);

  const decay = detectSetupDecay(setupName, setupTrades, anchorDate);
  if (decay) out.push(decay);

  return out.length === 0 ? null : out;
};

// Exports for tests
export {
  classifyStack,
  detectStackAlignmentEdge,
  detectHtfStackEdge,
  detectHtfStackEdgeForTf,
  detectSetupMfeLeakage,
  detectSetupDecay
};
